import { CohortType } from "encamp-prisma/generated/browser";

/**
 * Simplified cohort data needed for name generation
 */
export interface CohortNameData {
  id: string;
  type: CohortType;
  state?: string | null;
  fdName?: string | null;
  lepcName?: string | null;
  credentialUsername?: string | null;
  alternateIdValue?: string | null;
  members?: Array<{
    facility?: {
      state?: string | null;
      tenant?: {
        name?: string | null;
      } | null;
    } | null;
  }> | null;
}

/**
 * Get the display name for a cohort based on its type, using names for FD and LEPC cohorts
 * @param cohort The cohort to get the display name for
 * @returns The display name as a string
 * @throws Error if cohort type is missing or unknown
 */
export function getCohortDisplayIdentifier(cohort: CohortNameData): string {
  if (!cohort.type) {
    throw new Error("Cohort type is required");
  }

  switch (cohort.type) {
    case CohortType.STATE:
      return cohort.state ?? "";
    case CohortType.FIRE_DEPARTMENT:
      return cohort.fdName ?? "";
    case CohortType.LEPC:
      return cohort.lepcName ?? "";
    case CohortType.CREDENTIAL:
      return cohort.credentialUsername ?? "";
    case CohortType.ALTERNATE_ID:
      return cohort.alternateIdValue ?? "";
    default:
      throw new Error(`Unknown cohort type: ${cohort.type}`);
  }
}

/**
 * Generate the full cohort name in the format: "displayName - state - tenant"
 * @param cohort The cohort to generate the name for
 * @returns The formatted cohort name
 */
export function generateCohortName(cohort: CohortNameData): string {
  if (!cohort.members || cohort.members.length === 0) {
    return "Empty Cohort";
  }

  try {
    const displayName = getCohortDisplayIdentifier(cohort);

    // Get state and tenant from the first member's facility
    const firstMember = cohort.members[0];
    const facility = firstMember?.facility;
    const state = facility?.state || "Unknown State";
    const tenant = facility?.tenant?.name || "Unknown Tenant";

    return `${displayName} - ${state} - ${tenant}`;
  } catch (error) {
    console.error("Error generating cohort name:", error);
    return "Unknown Cohort";
  }
}
